/**
 * `useT()` hook + locale provider.
 *
 * Locale resolution order:
 *   sessionStorage["piggy.locale"] → navigator.language → "en".
 *
 * Tenants can ship a per-tenant default later via the branding row.
 */

import { useEffect, useState } from "react";

import { SUPPORTED_LOCALES, t, type Locale } from "./messages";

const STORAGE_KEY = "piggy.locale";


export function detectLocale(): Locale {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
  } catch {
    /* sessionStorage may be locked down; fall through */
  }
  const nav = (navigator.language || "en").slice(0, 2) as Locale;
  if (SUPPORTED_LOCALES.includes(nav)) return nav;
  return "en";
}


export function setLocale(locale: Locale): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* noop */
  }
  window.dispatchEvent(new CustomEvent("piggy:locale-change", { detail: locale }));
}


export function useT(): { t: (key: string) => string; locale: Locale } {
  const [locale, setL] = useState<Locale>(() => detectLocale());
  useEffect(() => {
    function onChange(e: Event) {
      setL((e as CustomEvent<Locale>).detail);
    }
    window.addEventListener("piggy:locale-change", onChange);
    return () => window.removeEventListener("piggy:locale-change", onChange);
  }, []);
  return { t: (key) => t(key, locale), locale };
}

export { t, SUPPORTED_LOCALES, type Locale } from "./messages";
